import React from 'react'
import styled from 'styled-components'

import { CREDIT_REQUEST_POPUP_COPY, HOW_CREDITS_WORK_URL } from 'containers/Agents/creditConstants'

import Button from 'components/lib/DesignSystem/V3/Button'

import WalletCoinsIllustration from './WalletCoinsIllustration'

const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 480px;
  max-width: 100%;
  background: var(--color-elevated);
  border-radius: 16px;
  box-shadow:
    0px 20px 25px -5px rgba(0, 0, 0, 0.1),
    0px 8px 10px -6px rgba(0, 0, 0, 0.1);
  font-family: Poppins, sans-serif;
  overflow: hidden;
`

const Header = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px 20px;
  border-bottom: 1px solid var(--card-border-color);
`

const CloseButton = styled.button`
  appearance: none;
  background: transparent;
  border: 0;
  padding: 0;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #223063;

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
    border-radius: 4px;
  }
`

const Body = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 28px 20px 20px;
  border-bottom: 1px solid var(--card-border-color);
`

const Centered = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: flex-start;
  width: 100%;
`

const TextStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
  text-align: left;
  max-width: 346px;
  width: 100%;
`

const Title = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 500;
  line-height: 32px;
  color: #223063;
`

const BodyLine = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: var(--t-colors-blue-950, #151b56);
`

const Achievements = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`

const AchievementsTitle = styled.p`
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  color: var(--t-colors-blue-950, #151b56);
`

const StatRow = styled.div`
  display: flex;
  gap: 6px;
  width: 100%;
`

const StatCard = styled.div`
  display: flex;
  flex: 1 1 0;
  min-width: 0;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 4px;
  padding: 12px 8px;
  background: var(--t-colors-blue-50, #f2f5fe);
  border-radius: 14px;
  height: 62px;
`

const StatValue = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  color: var(--t-colors-blue-950, #151b56);
  text-align: left;
`

const StatLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--color-text);
`

const StatLabel = styled.span`
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: var(--color-text);
`

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
`

const HelpLink = styled.button`
  appearance: none;
  background: transparent;
  border: 0;
  padding: 0;
  font: inherit;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: var(--t-colors-blue-950, #151b56);
  cursor: pointer;
  text-align: left;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
    border-radius: 2px;
  }
`

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 6L18 18M18 6L6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
)

const ClockIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1" />
    <path d="M6 3.5V6L7.5 7" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
  </svg>
)

const ProfileIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="6" cy="6" r="5" fill="currentColor" />
    <circle cx="6" cy="5" r="1.5" fill="var(--color-surface-foreground)" />
    <path
      d="M3 9C3.6 7.8 4.7 7.2 6 7.2C7.3 7.2 8.4 7.8 9 9"
      stroke="var(--color-surface-foreground)"
      strokeWidth="0.9"
      strokeLinecap="round"
    />
  </svg>
)

const ShieldIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6 1L10 2.5V6.2C10 8.4 8.3 10.3 6 11C3.7 10.3 2 8.4 2 6.2V2.5L6 1Z"
      stroke="currentColor"
      strokeWidth="1"
      strokeLinejoin="round"
    />
  </svg>
)

const ICON_BY_TYPE = {
  time: ClockIcon,
  people: ProfileIcon,
  tasks: ShieldIcon,
}

const StatItem = ({ value, label, iconType, id }) => {
  const Icon = ICON_BY_TYPE[iconType]
  return (
    <StatCard id={id}>
      <StatValue>{value}</StatValue>
      <StatLabelRow>
        {Icon && <Icon />}
        <StatLabel>{label}</StatLabel>
      </StatLabelRow>
    </StatCard>
  )
}

const handleHowCreditsWork = (custom) => {
  if (typeof custom === 'function') return custom()
  globalThis?.open?.(HOW_CREDITS_WORK_URL, '_blank', 'noopener,noreferrer')
  return undefined
}

const buildAchievementsFromStats = (stats) => {
  if (!stats) return []
  return [
    { id: 'time', value: `${stats.timeSavedHours}h`, label: 'Time saved', iconType: 'time' },
    {
      id: 'people',
      value: String(stats.peopleUsingAI),
      label: 'People using AI',
      iconType: 'people',
    },
    {
      id: 'tasks',
      value: String(stats.tasksCompleted),
      label: 'Tasks completed',
      iconType: 'tasks',
    },
  ]
}

/** Figma 4584:149739 — paid admin "Your team has reached its credit limit" toast with achievements (Purchase Flow §3.4, admin-only). */
const CreditsExhaustedPaidToast = ({
  requesterName,
  otherRequestersCount = 0,
  achievementsStats,
  achievementsTitle,
  achievements,
  isAddCreditsLoading = false,
  isAddCreditsDisabled = false,
  // Purchase Flow §3.4 — primary CTA is 'Add more credits' (PLG §3.2 uses 'Add credits').
  addCreditsLabel = 'Add more credits',
  onClose,
  onAddCredits,
  onHowCreditsWorks,
  id = 'credits-exhausted-paid-toast',
}) => {
  const copy = CREDIT_REQUEST_POPUP_COPY.PAID
  const subtext = copy.subtext({ userName: requesterName, otherCount: otherRequestersCount })
  const resolvedTitle =
    achievementsTitle ||
    (achievementsStats?.monthName
      ? `Your ${achievementsStats.monthName} achievements:`
      : 'Your achievements:')
  const resolvedAchievements = achievements || buildAchievementsFromStats(achievementsStats)
  const showAchievements = resolvedAchievements.length > 0

  return (
    <Card id={id} role="dialog" aria-modal="true" aria-labelledby={`${id}-title`}>
      <Header>
        <CloseButton id={`${id}-close`} type="button" aria-label="Close" onClick={onClose}>
          <CloseIcon />
        </CloseButton>
      </Header>
      <Body>
        <WalletCoinsIllustration scale={0.55} />
        <Centered>
          <TextStack>
            <Title id={`${id}-title`}>{copy.title}</Title>
            <BodyLine id={`${id}-body`}>{subtext}</BodyLine>
          </TextStack>
          {showAchievements && (
            <Achievements aria-labelledby={`${id}-achievements-title`}>
              <AchievementsTitle id={`${id}-achievements-title`}>{resolvedTitle}</AchievementsTitle>
              <StatRow>
                {resolvedAchievements.map((stat) => (
                  <StatItem
                    key={stat.id}
                    id={`${id}-stat-${stat.id}`}
                    value={stat.value}
                    label={stat.label}
                    iconType={stat.iconType}
                  />
                ))}
              </StatRow>
            </Achievements>
          )}
        </Centered>
      </Body>
      <Footer>
        <HelpLink
          id={`${id}-help`}
          type="button"
          onClick={() => handleHowCreditsWork(onHowCreditsWorks)}
        >
          How credits work?
        </HelpLink>
        <Button
          id={`${id}-add-credits`}
          size="medium"
          variant="primary"
          loading={isAddCreditsLoading}
          disabled={isAddCreditsDisabled}
          onClick={onAddCredits}
        >
          {addCreditsLabel}
        </Button>
      </Footer>
    </Card>
  )
}

export default CreditsExhaustedPaidToast
